import {Component, OnDestroy, OnInit, Optional} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {LayoutService} from '@ngmedax/layout';
import {MediaCenter, MediaCenterService} from '@ngmedax/asset';
import {TRANSLATION_THEMING_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {AdminTheming} from '../../../types';
import {ADMIN_FONTS, UiSettingsService} from '../services/ui-settings.service';
import {adminPalette} from '../services/admin-palette';
import {CustomCssDialogComponent} from './custom-css-dialog.component';

// hack to inject decorator declarations. must occur before class declaration!
export interface AdminThemingComponent extends Translatable {}

/**
 * Brand of the administration interface: main color, font, logo and own css, analog to the filling app
 */
@Component({
  selector: 'app-admin-theming',
  templateUrl: './admin-theming.component.html',
  styleUrls: ['./theming.component.css', './admin-theming.component.css']
})
@Translatable({scope: TRANSLATION_THEMING_SCOPE, keys: KEYS})
export class AdminThemingComponent implements OnInit, OnDestroy {
  public readonly fonts = ADMIN_FONTS;

  public theming: AdminTheming = {active: false, mainColor: '', font: '', logo: null, logoAsset: null, customCss: ''};

  /** The whole interface shows the unsaved brand */
  public livePreview = false;

  public assetLogoOptions = {
    accept: 'image/*',
    bucketId: 'mobile-theme-files',
    filter: {type: 'image/*'},
    callback: async (selectedFile: MediaCenter.Asset) => {
      this.theming.logoAsset = selectedFile;
      this.theming.logo = await this.mediaCenter.getImageData(selectedFile, this.assetLogoOptions.bucketId, true) || null;
      this.updateLivePreview();
    }
  };

  public constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private mediaCenter: MediaCenterService,
    private uiSettings: UiSettingsService,
    private modalService: NgbModal,
    private layout: LayoutService
  ) {}

  public async ngOnInit() {
    await this.uiSettings.refresh();
    const stored = this.uiSettings.getSettings().adminTheming;
    stored && (this.theming = {...this.theming, ...stored});
    this.theming.font && this.uiSettings.loadFonts();
  }

  /** Leaving the page without saving shows the stored brand again */
  public ngOnDestroy() {
    this.livePreview && this.uiSettings.preview(null);
  }

  /** Brand tokens for the small preview, light or dark like the current interface */
  public get previewTokens(): {[token: string]: string} {
    const palette = adminPalette(this.theming.mainColor);
    const dark = document.body.classList.contains('dark-theme');
    const tokens: {[token: string]: string} = palette ? {...(dark ? palette.dark : palette.light)} : {};
    this.theming.font && (tokens['--mm-font-family'] = `"${this.theming.font}", sans-serif`);
    return tokens;
  }

  public get validColor(): boolean {
    return !!adminPalette(this.theming.mainColor);
  }

  public canSave(): boolean {
    return this.validColor || !!this.theming.logo || !!this.customCssLines;
  }

  /** Number of non empty lines of the custom css */
  public get customCssLines(): number {
    return (this.theming.customCss || '').split('\n').filter(line => line.trim()).length;
  }

  public onMainColorChange(value: string) {
    this.theming.mainColor = value;
    this.updateLivePreview();
  }

  public onFontChange() {
    this.theming.font && this.uiSettings.loadFonts();
    this.updateLivePreview();
  }

  public onOpenMediaCenterModalForLogo() {
    this.mediaCenter && this.mediaCenter.openModal(this.assetLogoOptions);
  }

  public onDeleteLogo() {
    this.theming.logo = null;
    this.theming.logoAsset = null;
    this.updateLivePreview();
  }

  public async onOpenCustomCssDialog() {
    const modalRef = this.modalService.open(CustomCssDialogComponent, {size: 'lg', scrollable: true});
    modalRef.componentInstance.customCss = this.theming.customCss;

    try {
      this.theming.customCss = await modalRef.result;
      this.updateLivePreview();
    } catch (err) {
      // cancelled
    }
  }

  /** Shows the unsaved brand on the whole interface or the stored one again */
  public onToggleLivePreview() {
    this.livePreview = !this.livePreview;
    this.uiSettings.preview(this.livePreview ? {...this.theming, active: true} : null);
  }

  /** Switches the stored brand on or off right away, like the switch of the filling app */
  public async onToggle() {
    this.theming.active = !this.theming.active;
    try {
      const stored = this.uiSettings.getSettings().adminTheming || this.theming;
      await this.uiSettings.saveAdminTheming({...stored, active: this.theming.active});
      this.livePreview = false;
      alert(this._(this.theming.active ? 'Theming wurde aktiviert.' : 'Theming wurde deaktiviert.'));
    } catch (error) {
      console.error(error);
      this.theming.active = !this.theming.active;
      alert(this._('Fehler beim Speichern der Einstellungen.'));
    }
  }

  public async onSave() {
    try {
      this.layout.showPreloader(this._('Wird gespeichert...'));
      await this.uiSettings.saveAdminTheming({...this.theming});
      this.livePreview = false;
      this.layout.hidePreloader();
      alert(this._('Die Einstellungen wurden gespeichert.'));
    } catch (error) {
      console.error(error);
      this.layout.hidePreloader();
      alert(this._('Fehler beim Speichern der Einstellungen.'));
    }
  }

  private updateLivePreview() {
    this.livePreview && this.uiSettings.preview({...this.theming, active: true});
  }

  public _!: (text: string, variables?: any) => string;
  public readonly KEYS = KEYS;
}
